import { stringToDocumentFragment, activateInlineScripts } from "@otto-ec/global-resources/dom";
import {
  flatMap,
  pipe,
  succeed,
  tap,
  catchType,
  allSync,
  tapSpread,
  fromNullable,
  tapLeft,
  tapRight,
  fromCondition,
  tapAsync,
} from "@otto-ec/assets-core-utils/nano-effect";
import { untrack } from "svelte";
import { sheetScope } from "../utils/props";
import { getPropsFromContent, parseLegacyContent, updatePropsFromContent } from "./parser";
import type { OcSheetV1Props } from "../SheetV1.types.g";
import { LoadError } from "./loadError";
import { fetchFromUrl } from "./fetchUtils";
import { dispatchSheetEvent } from "../event/event.dispatch";
import {
  CONTENT_APPLY,
  CONTENT_DISCARD,
  CONTENT_ERROR,
  CONTENT_FETCH,
  CONTENT_PARSE,
} from "../event/event.name.defs";
import { assertContentURL } from "./assert";
import type { OcSheetV1InstanceState } from "../instance/stack.svelte";
import { stack } from "../stack/stack.global.svelte";

const log = /*           */ sheetScope.scope("external-content");

/*                                                                        */
export function useExternalContent(
  host: HTMLOcSheetV1Element,
  init: {
    /*                                                         */
    url: () => string | undefined;

    /*                                               */
    allowedCodes: () => number[];

    /*                                                       */
    invalidateOnInactive: () => boolean;
  },

  instanceState: OcSheetV1InstanceState,
) {
  const state = $state({
    /*                                                                                  */
    validUrl: null as string | null,

    /*                                                                                */
    rawContent: null as string | null,
    /*                                                                 */
    rawError: null as Error | LoadError | null,

    /*                                                                                    */
    loadedContent: null as DocumentFragment | null,
    /*                                                                                        */
    errorContent: null as null | DocumentFragment,

    /*                                                                              */
    externalOptions: null as Partial<OcSheetV1Props> | null,
    /*                                                                 */
    externalContent: null as DocumentFragment | null,

    /*                                                                       */
    isApplied: false,
  });

  /**
 *
 *
 */
  let currentSheetDom: DocumentFragment | null = null;

  /*                                                                    */
  /*                                                                       */
  $effect(() => {
    const urlProp = init.url();

    /*                                                        */
    if (!urlProp) return;

    pipe(
      assertContentURL(urlProp),

      /*                                                         */
      /*                             */
      tap((url) => {
        state.rawContent = null;
        state.rawError = null;

        /*                                                                                */
        state.validUrl = import.meta.env.DEV && import.meta.env.STORYBOOK ? `/dev${url}` : url;
      }),

      /*                       */
      tapLeft((error) => {
        if (import.meta.env.DEV) log.error("Error validating external content url", error);
        state.validUrl = null;
        state.rawError = error;
      }),
    );
  });

  /*                                                                              */
  /*                                                                                                 */
  $effect(() => {
    /*                                                    */
    if (!init.invalidateOnInactive() || !state.validUrl) return;
    /*                                    */
    if (instanceState.isCurrent || state.rawContent === null) return;

    dispatchSheetEvent(CONTENT_DISCARD, host, { info: stack.info });

    /*                                           */
    state.externalContent = null;
    state.loadedContent = null;
    state.rawContent = null;
    state.rawError = null;
  });

  /*                                                                               */
  /*                                                                                        */
  $effect(() => {
    /*                         */
    if (state.rawError !== null || state.rawContent !== null || !currentSheetDom) return;

    /*                                                                   */
    state.isApplied = false;
    host.replaceChildren(currentSheetDom);
  });

  /*                                                           */
  /*                                                  */
  $effect(() => {
    const validUrl = state.validUrl;
    if (!instanceState.isCurrent || !validUrl || state.rawContent !== null) return;

    pipe(
      fetchFromUrl(validUrl, untrack(init.allowedCodes)),

      /*                                                              */
      tap((raw) => {
        state.isApplied = false;

        /*                                           */
        const payload = { url: validUrl, raw, info: stack.info };
        dispatchSheetEvent(CONTENT_FETCH, host, payload);

        /*                                  */
        state.rawContent = payload.raw;

        /*                                              */
        state.loadedContent = stringToDocumentFragment(state.rawContent);
      }),

      /*                                      */
      tapLeft((error) => {
        state.rawError = error;
      }),
    );
  });

  /*                                                           */
  $effect(() => {
    pipe(
      /*                                    */
      fromCondition(!state.rawError, null, state.rawError!),

      /*                           */
      tapRight((nullValue) => {
        state.errorContent = nullValue;
      }),

      /*                                                  */
      /*                                              */
      tapLeft((error) => {
        log.error("Error loading external content", error);

        dispatchSheetEvent(CONTENT_ERROR, host, {
          raw: state.rawContent ?? undefined,
          url: init.url(),
          error,
        });
      }),

      /*                                 */
      catchType(LoadError, (error) => {
        if (error.errorContent) {
          /*                                      */
          state.errorContent = stringToDocumentFragment(error.errorContent);
        }
        return succeed(null);
      }),
    );
  });

  /*                                               */
  $effect(() => {
    pipe(
      /*                                   */
      fromNullable(state.loadedContent ?? state.errorContent),

      /*                               */
      flatMap((content) => allSync([getPropsFromContent(content), parseLegacyContent(content)])),

      /*                                                              */
      tapSpread((externalOptions, externalContent) => {
        dispatchSheetEvent(CONTENT_PARSE, host, {
          options: externalOptions,
          parsed: externalContent,
          raw: untrack(() => state.rawContent!),
          url: untrack(() => state.validUrl!),
          info: stack.info,
        });

        state.externalOptions = externalOptions;
        state.externalContent = externalContent;
      }),

      /*                                                             */
      tapLeft((empty) => {
        state.externalOptions = empty;
        state.externalContent = empty;
      }),
    );
  });

  /*                                 */
  $effect(() => {
    /*                                       */
    if (!state.externalOptions) return;

    /*                         */
    updatePropsFromContent(host, state.externalOptions);
  });

  /*                                         */
  $effect(() => {
    /*                                                         */
    const { isCurrent } = instanceState;
    const { externalContent, isApplied } = state;

    /*                                                                               */
    if (!externalContent || !isCurrent || isApplied) return;

    /*                                      */
    if (!currentSheetDom) currentSheetDom = new DocumentFragment();

    if (import.meta.env.DEV) log.debug("Applying external content to sheet", externalContent);
    pipe(
      succeed([externalContent, currentSheetDom]),
      /*                                            */
      tap(([, dom]) => dom.replaceChildren(...host.childNodes)),
      /*                         */
      tap(([content]) => host.append(content)),
      /*                                           */
      tap(() => dispatchSheetEvent(CONTENT_APPLY, host, { info: stack.info })),
      /*                                        */
      tapAsync(() => activateInlineScripts(host)),
      /*                                                                 */
      /*                                          */
      tap(() => (state.isApplied = true)),
    );
  });

  return state;
}
